import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const API = '/api';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : 'N/A';
const initials = name => (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const statusColor = s => ({ approved:'#A7F432', rejected:'#ff6b6b', pending:'#F5A623', not_submitted:'#8a9a97' })[s]||'#8a9a97';
const statusBg    = s => ({ approved:'rgba(167,244,50,0.12)', rejected:'rgba(255,80,80,0.12)', pending:'rgba(245,166,35,0.12)', not_submitted:'rgba(138,154,151,0.12)' })[s]||'rgba(138,154,151,0.12)';

export default function SelfieVerification({ toast }) {
  const { token } = useApp();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState(false);
  const [pagination, setPagination] = useState(null);

  const load = useCallback(async (status='pending') => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/players/pending-selfies?status=${status}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      const payload = d.data?.data !== undefined ? d.data : d;
      setPlayers(payload.data||[]);
      setPagination(payload.pagination||null);
    } catch(e) { toast?.(e.message,'error'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(filter); }, [load, filter]);

  const review = async (id, status) => {
    setActing(true);
    try {
      const res = await fetch(`${API}/players/${id}/selfie-verification`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, reason }),
      });
      const d = await res.json();
      if (d.success) { toast?.(`Selfie ${status}`); setSelected(null); setReason(''); load(filter); }
      else { toast?.(d.message, 'error'); }
    } catch(e) { toast?.(e.message,'error'); }
    finally { setActing(false); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--text-primary)', margin:0 }}>Selfie Verification</h1>
          <p style={{ color:'var(--text-dim)', fontSize:13, margin:'4px 0 0' }}>Review and approve player identity verifications</p>
        </div>
        {pagination && <div style={{ fontSize:12, color:'var(--text-dim)', background:'var(--bg-card)', padding:'6px 14px', borderRadius:8, border:'1px solid var(--border)' }}>{pagination.total} {filter}</div>}
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:16, background:'var(--bg-card)', padding:4, borderRadius:10, border:'1px solid var(--border)', width:'fit-content' }}>
        {['pending','approved','rejected','not_submitted'].map(s => (
          <button key={s} onClick={() => { setFilter(s); load(s); }}
            style={{ background: filter===s?'rgba(167,244,50,0.15)':'none', color: filter===s?'#A7F432':'var(--text-dim)',
              border: filter===s?'1px solid rgba(167,244,50,0.3)':'1px solid transparent',
              borderRadius:7, padding:'6px 14px', fontSize:12, fontWeight:600, cursor:'pointer', textTransform:'capitalize' }}>
            {s.replace('_',' ')}
          </button>
        ))}
      </div>

      {loading ? <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading...</div>
      : players.length===0 ? <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>No {filter} verifications</div>
      : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>
          {players.map(p => (
            <div key={p.id} onClick={() => setSelected(p)}
              style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:16, cursor:'pointer' }}>
              <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:12 }}>
                <div style={{ width:44, height:44, borderRadius:'50%', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg,#A7F432,#5a9e1a)', fontSize:14, fontWeight:700, color:'#000' }}>
                  {p.pfp ? <img src={p.pfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(p.name)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:600, color:'var(--text-primary)' }}>{p.name}</div>
                  <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:2 }}>{p.email}</div>
                  <span style={{ fontSize:10, padding:'2px 8px', borderRadius:20, marginTop:4, display:'inline-block', background:statusBg(p.selfieVerificationStatus), color:statusColor(p.selfieVerificationStatus), fontWeight:600 }}>
                    {p.selfieVerificationStatus?.replace('_',' ')}
                  </span>
                </div>
              </div>
              {p.selfieUrl ? (
                <div style={{ borderRadius:8, overflow:'hidden', height:180, background:'rgba(0,0,0,0.3)' }}>
                  <img src={p.selfieUrl} alt="Selfie" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                </div>
              ) : (
                <div style={{ borderRadius:8, height:180, background:'rgba(255,255,255,0.04)', display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.2)', fontSize:13 }}>
                  No selfie submitted
                </div>
              )}
              {p.selfieRejectionReason && <div style={{ marginTop:8, fontSize:11, color:'#ff6b6b', background:'rgba(255,80,80,0.08)', borderRadius:6, padding:'6px 10px' }}>Reason: {p.selfieRejectionReason}</div>}
              {p.selfieVerifiedAt && <div style={{ marginTop:6, fontSize:11, color:'var(--text-dim)' }}>Reviewed: {fmtDate(p.selfieVerifiedAt)}</div>}
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:24 }}
          onClick={e => e.target===e.currentTarget && setSelected(null)}>
          <div style={{ background:'#0d1a1a', borderRadius:16, width:'100%', maxWidth:700, border:'1px solid rgba(255,255,255,0.07)', overflow:'hidden' }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:17, fontWeight:700, color:'#fff' }}>{selected.name}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background:'rgba(255,255,255,0.05)', color:'rgba(255,255,255,0.5)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'6px 12px', cursor:'pointer' }}>x</button>
            </div>

            <div style={{ padding:24, display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginBottom:8, fontWeight:600, textTransform:'uppercase' }}>Profile Photo</div>
                <div style={{ borderRadius:10, overflow:'hidden', height:240, background:'rgba(255,255,255,0.04)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {selected.pfp ? <img src={selected.pfp} alt="Profile" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : <div style={{ fontSize:48, fontWeight:700, color:'#A7F432' }}>{initials(selected.name)}</div>}
                </div>
              </div>
              <div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginBottom:8, fontWeight:600, textTransform:'uppercase' }}>Submitted Selfie</div>
                <div style={{ borderRadius:10, overflow:'hidden', height:240, background:'rgba(255,255,255,0.04)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {selected.selfieUrl ? <img src={selected.selfieUrl} alt="Selfie" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : <div style={{ color:'rgba(255,255,255,0.2)', fontSize:13 }}>No selfie submitted</div>}
                </div>
              </div>
            </div>

            <div style={{ margin:'0 24px', background:'rgba(255,255,255,0.03)', borderRadius:10, padding:'12px 16px', display:'flex', gap:24, flexWrap:'wrap', marginBottom:16 }}>
              <div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Name</div><div style={{ fontSize:13, color:'#fff', fontWeight:600 }}>{selected.name}</div></div>
              <div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Status</div><span style={{ fontSize:12, padding:'2px 8px', borderRadius:20, background:statusBg(selected.selfieVerificationStatus), color:statusColor(selected.selfieVerificationStatus), fontWeight:600 }}>{selected.selfieVerificationStatus?.replace('_',' ')}</span></div>
            </div>

            {selected.selfieVerificationStatus !== 'approved' && (
              <div style={{ padding:'0 24px 16px' }}>
                <label style={{ fontSize:12, color:'rgba(255,255,255,0.4)', display:'block', marginBottom:6 }}>Rejection Reason (optional)</label>
                <input value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. Face not clearly visible..."
                  style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'10px 14px', color:'#fff', fontSize:13, outline:'none', boxSizing:'border-box' }} />
              </div>
            )}

            <div style={{ padding:'0 24px 24px', display:'flex', gap:10 }}>
              {selected.selfieVerificationStatus !== 'approved' && (
                <button onClick={() => review(selected.id, 'approved')} disabled={acting||!selected.selfieUrl}
                  style={{ flex:1, padding:14, background:'rgba(167,244,50,0.15)', color:'#A7F432', border:'1px solid rgba(167,244,50,0.3)', borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer', opacity:acting||!selected.selfieUrl?0.5:1 }}>
                  {acting?'...':'Approve — Face Matches'}
                </button>
              )}
              {selected.selfieVerificationStatus !== 'rejected' && (
                <button onClick={() => review(selected.id, 'rejected')} disabled={acting||!selected.selfieUrl}
                  style={{ flex:1, padding:14, background:'rgba(255,80,80,0.12)', color:'#ff6b6b', border:'1px solid rgba(255,80,80,0.3)', borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer', opacity:acting||!selected.selfieUrl?0.5:1 }}>
                  {acting?'...':'Reject — Does Not Match'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
